<template>
  <ContentWrap>
    <div class="hotel-head">
      <div>
        <h2>{{ sectionTitle }}</h2>
        <p>所有数据按当前 Merchant Token 的酒店授权范围隔离</p>
      </div>
      <el-select v-model="shopId" class="!w-240px" placeholder="选择酒店" @change="reload">
        <el-option v-for="shop in shops" :key="shop.id" :label="shop.name" :value="shop.id" />
      </el-select>
    </div>
  </ContentWrap>

  <template v-if="section === 'dashboard'">
    <el-row :gutter="16" v-loading="loading">
      <el-col v-for="card in dashboardCards" :key="card.label" :xs="12" :sm="8" :lg="4">
        <el-card shadow="never" class="metric-card">
          <span>{{ card.label }}</span
          ><strong>{{ card.value }}</strong>
        </el-card>
      </el-col>
    </el-row>
    <ContentWrap title="统计周期">
      <el-date-picker
        v-model="dashboardRange"
        type="daterange"
        value-format="YYYY-MM-DD"
        @change="loadDashboard"
      />
      <el-alert
        class="mt-16px"
        type="info"
        :closable="false"
        title="看板读取已归档的酒店日统计数据；未归档日期按 0 展示。"
      />
    </ContentWrap>
    <ContentWrap title="敏感资料变更">
      <el-alert
        class="mb-16px"
        type="info"
        :closable="false"
        title="酒店名称、地址、资质和结算主体需提交平台审核；审核通过前不会覆盖当前线上资料。"
      />
      <el-form :model="profileChangeForm" label-width="110px" class="policy-form">
        <el-form-item label="酒店名称" required
          ><el-input v-model="profileChangeForm.hotelName"
        /></el-form-item>
        <el-form-item label="酒店地址" required
          ><el-input v-model="profileChangeForm.address"
        /></el-form-item>
        <el-form-item label="资质 JSON" required
          ><el-input v-model="profileChangeForm.qualificationJson" type="textarea" :rows="4"
        /></el-form-item>
        <el-form-item label="结算主体" required
          ><el-input v-model="profileChangeForm.settlementSubject"
        /></el-form-item>
        <el-form-item label="变更原因" required
          ><el-input v-model="profileChangeForm.reason" type="textarea"
        /></el-form-item>
        <el-form-item
          ><el-button type="primary" :loading="saving" @click="submitProfileChange"
            >提交平台审核</el-button
          ></el-form-item
        >
      </el-form>
      <el-table :data="profileChanges" border>
        <el-table-column prop="version" label="版本" width="80" />
        <el-table-column prop="hotelName" label="酒店名称" min-width="160" />
        <el-table-column prop="address" label="地址" min-width="220" />
        <el-table-column prop="settlementSubject" label="结算主体" min-width="160" />
        <el-table-column prop="status" label="状态" width="100" />
        <el-table-column prop="auditRemark" label="审核意见" min-width="180" />
        <el-table-column prop="submitTime" label="提交时间" width="170" />
      </el-table>
    </ContentWrap>
  </template>

  <template v-else-if="section === 'room-types'">
    <ContentWrap>
      <div class="room-type-toolbar">
        <el-alert
          type="info"
          :closable="false"
          title="房型名称、图片或价格变更后会重新进入平台审核；未审核通过的房型不能上架。"
        />
        <div class="room-type-actions">
          <el-button @click="downloadRoomTypeTemplate">下载导入模板</el-button>
          <el-button @click="roomTypeImportInput?.click()">批量导入</el-button>
          <el-button @click="exportRoomTypes">导出</el-button>
          <el-button type="primary" @click="openRoomType()">新增房型</el-button>
          <input
            ref="roomTypeImportInput"
            type="file"
            accept=".xls,.xlsx"
            hidden
            @change="importRoomTypeFile"
          />
        </div>
      </div>
    </ContentWrap>
    <ContentWrap>
      <el-table :data="roomTypeRows" v-loading="loading">
        <el-table-column label="房型" min-width="240">
          <template #default="{ row }">
            <div class="room-type-name">
              <el-image :src="row.picUrl" fit="cover" />
              <div
                ><strong>{{ row.name }}</strong
                ><small>{{ row.introduction }}</small></div
              >
            </div>
          </template>
        </el-table-column>
        <el-table-column label="床型/面积" width="150">
          <template #default="{ row }"
            >{{ bedTypeLabel(row.bedType) }} / {{ row.roomSize }}㎡</template
          >
        </el-table-column>
        <el-table-column prop="maxGuests" label="入住人数" width="90" />
        <el-table-column label="价型" width="90">
          <template #default="{ row }">{{ row.skus?.length || 0 }} 个</template>
        </el-table-column>
        <el-table-column label="最低价" width="110">
          <template #default="{ row }">¥{{ roomTypeMinPrice(row) }}</template>
        </el-table-column>
        <el-table-column label="审核" width="110">
          <template #default="{ row }">
            <el-tag :type="auditStatusMeta(row.auditStatus).type">{{
              auditStatusMeta(row.auditStatus).label
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="销售状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'">{{
              row.status === 1 ? '已上架' : '已下架'
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="190" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openRoomType(row)">编辑</el-button>
            <el-button
              v-if="row.status !== 1 && [2, 5].includes(row.auditStatus)"
              link
              type="success"
              @click="changeRoomTypeSaleStatus(row, true)"
              >上架</el-button
            >
            <el-button
              v-if="row.status === 1"
              link
              type="warning"
              @click="changeRoomTypeSaleStatus(row, false)"
              >下架</el-button
            >
          </template>
        </el-table-column>
      </el-table>
    </ContentWrap>
  </template>

  <template v-else-if="section === 'rooms'">
    <ContentWrap>
      <el-form inline>
        <el-form-item label="房间号"
          ><el-input v-model="roomQuery.roomNo" clearable
        /></el-form-item>
        <el-form-item label="状态">
          <el-select v-model="roomQuery.roomState" clearable class="!w-140px">
            <el-option
              v-for="item in roomStates"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-button @click="loadRooms">查询</el-button>
        <el-button type="primary" @click="openRoom()">新增房间</el-button>
      </el-form>
    </ContentWrap>
    <ContentWrap>
      <el-table :data="rooms" v-loading="loading">
        <el-table-column prop="roomNo" label="房间号" />
        <el-table-column prop="floor" label="楼层" />
        <el-table-column prop="spuName" label="房型" />
        <el-table-column label="房态"
          ><template #default="{ row }">{{
            roomStateLabel(row.roomState)
          }}</template></el-table-column
        >
        <el-table-column prop="remark" label="备注" />
        <el-table-column label="操作" width="140">
          <template #default="{ row }">
            <el-button link type="primary" @click="openRoom(row)">编辑</el-button>
            <el-button link type="danger" @click="removeRoom(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        :total="roomTotal"
        v-model:page="roomQuery.pageNo"
        v-model:limit="roomQuery.pageSize"
        @pagination="loadRooms"
      />
    </ContentWrap>
  </template>

  <template v-else-if="section === 'inventory'">
    <ContentWrap>
      <el-form inline>
        <el-form-item label="房型">
          <el-select v-model="inventoryQuery.spuId" class="!w-240px" @change="loadInventory">
            <el-option
              v-for="item in roomTypes"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="日期">
          <el-date-picker
            v-model="inventoryQuery.range"
            type="daterange"
            value-format="YYYY-MM-DD"
            @change="loadInventory"
          />
        </el-form-item>
        <el-button type="primary" @click="openInventoryBatch">批量设置</el-button>
      </el-form>
    </ContentWrap>
    <ContentWrap>
      <el-table :data="inventory" v-loading="loading">
        <el-table-column prop="date" label="日期" />
        <el-table-column prop="price" label="房价" />
        <el-table-column prop="extraBedPrice" label="加床价" />
        <el-table-column prop="totalCount" label="总库存" />
        <el-table-column prop="availableCount" label="可售" />
        <el-table-column prop="lockedCount" label="锁定" />
        <el-table-column prop="soldCount" label="已售" />
      </el-table>
    </ContentWrap>
  </template>

  <template v-else-if="section === 'orders'">
    <ContentWrap>
      <el-form inline>
        <el-form-item label="订单号"
          ><el-input v-model="orderQuery.orderNo" clearable
        /></el-form-item>
        <el-form-item label="入住人"
          ><el-input v-model="orderQuery.guestName" clearable
        /></el-form-item>
        <el-button @click="loadOrders">查询</el-button>
        <el-button type="primary" @click="openQuickCheckIn">线下快速入住</el-button>
      </el-form>
    </ContentWrap>
    <ContentWrap>
      <el-table :data="orders" v-loading="loading">
        <el-table-column prop="orderNo" label="订单号" min-width="170" />
        <el-table-column prop="guestName" label="入住人" />
        <el-table-column prop="spuName" label="房型" />
        <el-table-column prop="roomNo" label="房间" />
        <el-table-column label="入住日期" min-width="180"
          ><template #default="{ row }"
            >{{ row.checkInDate }} 至 {{ row.checkOutDate }}</template
          ></el-table-column
        >
        <el-table-column prop="totalPrice" label="金额" />
        <el-table-column label="状态"
          ><template #default="{ row }">{{
            orderStatusLabel(row.status)
          }}</template></el-table-column
        >
        <el-table-column label="操作" width="290">
          <template #default="{ row }">
            <el-button link type="primary" @click="openOrderDetail(row)">详情</el-button>
            <el-button
              v-if="row.status === 1"
              link
              type="primary"
              @click="orderAction(row, 'confirm')"
              >确认</el-button
            >
            <el-button
              v-if="row.status === 2"
              link
              type="primary"
              @click="orderAction(row, 'check-in')"
              >入住</el-button
            >
            <el-button
              v-if="row.status === 3"
              link
              type="primary"
              @click="orderAction(row, 'check-out')"
              >离店</el-button
            >
            <el-button v-if="row.status < 3" link type="danger" @click="orderAction(row, 'cancel')"
              >取消</el-button
            >
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        :total="orderTotal"
        v-model:page="orderQuery.pageNo"
        v-model:limit="orderQuery.pageSize"
        @pagination="loadOrders"
      />
    </ContentWrap>
  </template>

  <template v-else-if="section === 'services'">
    <ContentWrap title="清洁任务">
      <el-table :data="cleaning" v-loading="loading">
        <el-table-column prop="roomNo" label="房间" />
        <el-table-column prop="assigneeName" label="执行人" />
        <el-table-column prop="status" label="状态" />
        <el-table-column label="操作" width="260">
          <template #default="{ row }">
            <el-button v-if="row.status === 0" link type="primary" @click="openServiceAssign(row, 'cleaning')"
              >派单</el-button
            >
            <el-button v-if="row.status === 1" link @click="cleaningAction(row, 'start')"
              >开始</el-button
            >
            <el-button v-if="row.status === 2" link @click="cleaningAction(row, 'complete')"
              >完成</el-button
            >
            <el-button v-if="row.status === 3" link @click="cleaningAction(row, 'verify')"
              >验收</el-button
            >
            <el-button
              v-if="row.status < 4"
              link
              type="danger"
              @click="cleaningAction(row, 'cancel')"
              >取消</el-button
            >
          </template>
        </el-table-column>
      </el-table>
    </ContentWrap>
    <ContentWrap title="维修工单">
      <el-button type="primary" class="mb-12px" @click="maintenanceVisible = true"
        >新增报修</el-button
      >
      <el-table :data="maintenance" v-loading="loading">
        <el-table-column prop="orderNo" label="工单号" />
        <el-table-column prop="roomNo" label="房间" />
        <el-table-column prop="faultType" label="故障类型" />
        <el-table-column prop="faultDesc" label="故障描述" />
        <el-table-column prop="status" label="状态" />
        <el-table-column label="操作" width="260">
          <template #default="{ row }">
            <el-button v-if="row.status === 0" link type="primary" @click="openServiceAssign(row, 'maintenance')"
              >派单</el-button
            >
            <el-button v-if="row.status === 1" link @click="maintenanceAction(row, 'start')"
              >开始</el-button
            >
            <el-button v-if="row.status === 2" link @click="openMaintenanceComplete(row)"
              >完成</el-button
            >
            <el-button v-if="row.status === 3" link @click="maintenanceAction(row, 'verify')"
              >验收</el-button
            >
            <el-button link type="danger" @click="removeMaintenance(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </ContentWrap>
  </template>

  <template v-else-if="section === 'finance'">
    <ContentWrap>
      <div class="finance-filter">
        <el-date-picker
          v-model="financeRange"
          type="daterange"
          value-format="YYYY-MM-DD"
          @change="loadFinance"
        />
        <el-button type="primary" @click="loadFinance">刷新</el-button>
      </div>
      <el-alert
        v-if="financeSummary.operationMode === 'SELF_OPERATED'"
        class="mt-12px"
        type="info"
        :closable="false"
        title="平台自营酒店不进入外部商户钱包；订单收入与佣金仍保留订单级核算明细。"
      />
    </ContentWrap>
    <el-row :gutter="16" v-loading="loading">
      <el-col v-for="card in financeCards" :key="card.label" :xs="12" :sm="8" :lg="4">
        <el-card shadow="never" class="metric-card"
          ><span>{{ card.label }}</span
          ><strong>{{ card.value }}</strong></el-card
        >
      </el-col>
    </el-row>
    <ContentWrap>
      <el-tabs v-model="financeTab" @tab-change="loadFinance">
        <el-tab-pane label="订单结算明细" name="settlement">
          <el-table :data="settlementRows" v-loading="loading">
            <el-table-column prop="businessNo" label="业务号" min-width="230" />
            <el-table-column prop="orderId" label="订单ID" min-width="150" />
            <el-table-column prop="businessType" label="类型" width="90" />
            <el-table-column label="实付/退款" width="120"
              ><template #default="{ row }"
                >¥{{ formatMoney(row.grossAmount) }}</template
              ></el-table-column
            >
            <el-table-column label="平台佣金" width="120"
              ><template #default="{ row }"
                >¥{{ formatMoney(row.platformAmount) }}</template
              ></el-table-column
            >
            <el-table-column label="商户应收" width="120"
              ><template #default="{ row }"
                >¥{{ formatMoney(row.merchantAmount) }}</template
              ></el-table-column
            >
            <el-table-column prop="commissionRate" label="佣金率(%)" width="105" />
            <el-table-column prop="createTime" label="记账时间" min-width="170" />
          </el-table>
          <Pagination
            v-model:page="settlementQuery.pageNo"
            v-model:limit="settlementQuery.pageSize"
            :total="settlementTotal"
            @pagination="loadSettlementDetails"
          />
        </el-tab-pane>
        <el-tab-pane label="周期结算批次" name="batch">
          <el-table :data="batchRows" v-loading="loading">
            <el-table-column prop="batchNo" label="批次号" min-width="260" />
            <el-table-column prop="settleCycle" label="周期" width="90" />
            <el-table-column prop="cutoffTime" label="截止时间" min-width="170" />
            <el-table-column prop="detailCount" label="明细数" width="80" />
            <el-table-column label="结算金额" width="120">
              <template #default="{ row }">¥{{ formatMoney(row.merchantAmount) }}</template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="100" />
            <el-table-column label="对账" width="105">
              <template #default="{ row }">
                <el-tag :type="row.reconciliationStatus === 'MATCHED' ? 'success' : 'danger'">
                  {{ row.reconciliationStatus }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="差额" width="120">
              <template #default="{ row }">¥{{ formatMoney(row.differenceAmount) }}</template>
            </el-table-column>
            <el-table-column prop="errorMessage" label="异常说明" min-width="200" />
          </el-table>
          <Pagination
            v-model:page="batchQuery.pageNo"
            v-model:limit="batchQuery.pageSize"
            :total="batchTotal"
            @pagination="loadBatches"
          />
        </el-tab-pane>
        <el-tab-pane label="商户资金账单" name="bill">
          <el-table :data="billRows" v-loading="loading">
            <el-table-column prop="billNo" label="账单号" min-width="180" />
            <el-table-column prop="orderId" label="订单ID" min-width="150" />
            <el-table-column prop="billType" label="账单类型" width="100" />
            <el-table-column label="变动金额" width="120"
              ><template #default="{ row }"
                >¥{{ formatMoney(row.amount) }}</template
              ></el-table-column
            >
            <el-table-column label="变动后余额" width="130"
              ><template #default="{ row }"
                >¥{{ formatMoney(row.balanceAfter) }}</template
              ></el-table-column
            >
            <el-table-column prop="description" label="说明" min-width="220" />
            <el-table-column prop="createTime" label="记账时间" min-width="170" />
          </el-table>
          <Pagination
            v-model:page="billQuery.pageNo"
            v-model:limit="billQuery.pageSize"
            :total="billTotal"
            @pagination="loadBills"
          />
        </el-tab-pane>
        <el-tab-pane label="电子发票" name="invoice">
          <el-table :data="invoiceRows" v-loading="loading">
            <el-table-column prop="applicationNo" label="申请号" min-width="210" />
            <el-table-column prop="orderId" label="订单 ID" min-width="150" />
            <el-table-column prop="title" label="抬头" min-width="180" />
            <el-table-column prop="taxNo" label="税号" min-width="180" />
            <el-table-column prop="email" label="邮箱" min-width="180" />
            <el-table-column label="金额" width="110">
              <template #default="{ row }">¥{{ formatMoney(row.amount) }}</template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="120" />
            <el-table-column prop="invoiceNo" label="发票号" min-width="150" />
            <el-table-column label="操作" width="220" fixed="right">
              <template #default="{ row }">
                <el-button
                  v-if="row.status === 'APPLIED'"
                  link
                  type="success"
                  @click="issueInvoice(row)"
                  >开具</el-button
                >
                <el-button
                  v-if="row.status === 'APPLIED'"
                  link
                  type="danger"
                  @click="rejectInvoice(row)"
                  >驳回</el-button
                >
                <el-button
                  v-if="row.status === 'RED_APPLIED'"
                  link
                  type="warning"
                  @click="completeInvoiceRed(row)"
                  >完成冲红</el-button
                >
              </template>
            </el-table-column>
          </el-table>
          <Pagination
            v-model:page="invoiceQuery.pageNo"
            v-model:limit="invoiceQuery.pageSize"
            :total="invoiceTotal"
            @pagination="loadInvoices"
          />
        </el-tab-pane>
      </el-tabs>
    </ContentWrap>
  </template>

  <template v-else-if="section === 'settings'">
    <ContentWrap title="运营策略">
      <el-form :model="policy" label-width="140px" class="policy-form" v-loading="loading">
        <el-form-item label="默认入住时间"
          ><el-time-select v-model="policy.checkInTime" start="00:00" step="00:30" end="23:30"
        /></el-form-item>
        <el-form-item label="默认退房时间"
          ><el-time-select v-model="policy.checkOutTime" start="00:00" step="00:30" end="23:30"
        /></el-form-item>
        <el-form-item label="自动确认"
          ><el-switch v-model="policy.autoConfirm" :active-value="1" :inactive-value="0"
        /></el-form-item>
        <el-form-item label="超卖限制"
          ><el-input-number v-model="policy.oversellLimit" :min="0"
        /></el-form-item>
        <el-form-item label="取消政策"
          ><el-input v-model="policy.cancelPolicy" type="textarea"
        /></el-form-item>
        <el-form-item label="押金政策"
          ><el-input v-model="policy.depositPolicy" type="textarea"
        /></el-form-item>
        <el-form-item label="钟点房"
          ><el-switch v-model="policy.hourlyRoomEnabled" :active-value="1" :inactive-value="0"
        /></el-form-item>
        <el-form-item label="多人入住"
          ><el-switch v-model="policy.multiGuestEnabled" :active-value="1" :inactive-value="0"
        /></el-form-item>
        <el-form-item label="儿童政策"><el-input v-model="policy.childPolicy" /></el-form-item>
        <el-form-item label="宠物政策"><el-input v-model="policy.petPolicy" /></el-form-item>
        <el-form-item
          ><el-button type="primary" :loading="saving" @click="savePolicy"
            >保存运营策略</el-button
          ></el-form-item
        >
      </el-form>
      <el-alert
        type="warning"
        :closable="false"
        title="经营模式、佣金率、结算周期由平台合同控制，商家端不可修改。"
      />
    </ContentWrap>
  </template>

  <Dialog
    v-model="roomTypeVisible"
    :title="roomTypeForm.id ? '编辑房型与价型' : '新增房型与价型'"
    width="920px"
  >
    <el-form :model="roomTypeForm" label-width="100px">
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="房型名称" required
            ><el-input v-model="roomTypeForm.name"
          /></el-form-item>
          <el-form-item label="搜索关键字" required
            ><el-input v-model="roomTypeForm.keyword"
          /></el-form-item>
          <el-form-item label="房型简介" required
            ><el-input v-model="roomTypeForm.introduction"
          /></el-form-item>
          <el-form-item label="房型详情" required
            ><el-input v-model="roomTypeForm.description" type="textarea" :rows="3"
          /></el-form-item>
          <el-form-item label="封面 URL" required
            ><el-input v-model="roomTypeForm.picUrl"
          /></el-form-item>
          <el-form-item label="轮播图 URL"
            ><el-input
              v-model="roomTypeForm.sliderPicsText"
              type="textarea"
              :rows="2"
              placeholder="每行一个 http(s) URL"
          /></el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="床型" required>
            <el-select v-model="roomTypeForm.bedType" class="!w-100%">
              <el-option
                v-for="item in bedTypes"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="房间面积" required
            ><el-input-number v-model="roomTypeForm.roomSize" :min="1" :max="10000" />
            ㎡</el-form-item
          >
          <el-form-item label="入住人数" required
            ><el-input-number v-model="roomTypeForm.maxGuests" :min="1" :max="20"
          /></el-form-item>
          <el-form-item label="客房设施">
            <el-select
              v-model="roomTypeFacilityCodes"
              multiple
              filterable
              collapse-tags
              class="!w-100%"
              placeholder="从平台设施目录选择"
            >
              <el-option
                v-for="facility in facilities"
                :key="facility.code"
                :label="`${facility.name}（${facility.code}）`"
                :value="facility.code"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="排序"
            ><el-input-number v-model="roomTypeForm.sort" :min="0"
          /></el-form-item>
        </el-col>
      </el-row>
      <el-divider content-position="left">价型与销售规则</el-divider>
      <el-table :data="roomTypeForm.skus" border>
        <el-table-column label="价型名称" min-width="130"
          ><template #default="{ row }"><el-input v-model="row.ratePlanName" /></template
        ></el-table-column>
        <el-table-column label="售价(元)" width="125"
          ><template #default="{ row }"
            ><el-input-number v-model="row.priceYuan" :min="0.01" :precision="2" /></template
        ></el-table-column>
        <el-table-column label="早餐" width="85"
          ><template #default="{ row }"
            ><el-input-number v-model="row.breakfastCount" :min="0" :max="20" /></template
        ></el-table-column>
        <el-table-column label="入住人数" width="95"
          ><template #default="{ row }"
            ><el-input-number v-model="row.maxGuests" :min="1" :max="20" /></template
        ></el-table-column>
        <el-table-column label="入住晚数" width="175"
          ><template #default="{ row }"
            ><div class="stay-range"
              ><el-input-number v-model="row.minStay" :min="1" :max="365" /><span>-</span
              ><el-input-number v-model="row.maxStay" :min="1" :max="365" /></div></template
        ></el-table-column>
        <el-table-column label="取消规则" min-width="180"
          ><template #default="{ row }"><el-input v-model="row.cancellationPolicy" /></template
        ></el-table-column>
        <el-table-column label="可售" width="70"
          ><template #default="{ row }"
            ><el-switch v-model="row.saleStatus" :active-value="1" :inactive-value="0" /></template
        ></el-table-column>
        <el-table-column label="" width="55"
          ><template #default="{ $index }"
            ><el-button link type="danger" @click="removeRatePlan($index)"
              >删除</el-button
            ></template
          ></el-table-column
        >
      </el-table>
      <el-button class="mt-12px" plain type="primary" @click="addRatePlan">添加价型</el-button>
    </el-form>
    <template #footer>
      <el-button @click="roomTypeVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="saveRoomType">保存并提交审核</el-button>
    </template>
  </Dialog>

  <Dialog v-model="roomVisible" :title="roomForm.id ? '编辑房间' : '新增房间'" width="520px">
    <el-form :model="roomForm" label-width="90px">
      <el-form-item label="房型" required
        ><el-select v-model="roomForm.spuId"
          ><el-option
            v-for="item in roomTypes"
            :key="item.id"
            :label="item.name"
            :value="item.id" /></el-select
      ></el-form-item>
      <el-form-item label="房间号" required><el-input v-model="roomForm.roomNo" /></el-form-item>
      <el-form-item label="楼层"><el-input-number v-model="roomForm.floor" /></el-form-item>
      <el-form-item label="房态"
        ><el-select v-model="roomForm.roomState"
          ><el-option
            v-for="item in roomStates"
            :key="item.value"
            :label="item.label"
            :value="item.value" /></el-select
      ></el-form-item>
      <el-form-item label="备注"><el-input v-model="roomForm.remark" /></el-form-item>
    </el-form>
    <template #footer
      ><el-button @click="roomVisible = false">取消</el-button
      ><el-button type="primary" :loading="saving" @click="saveRoom">保存</el-button></template
    >
  </Dialog>

  <Dialog v-model="inventoryVisible" title="批量设置量价" width="500px">
    <el-form :model="inventoryForm" label-width="100px">
      <el-form-item label="房价"
        ><el-input-number v-model="inventoryForm.price" :min="0" :precision="2"
      /></el-form-item>
      <el-form-item label="加床价"
        ><el-input-number v-model="inventoryForm.extraBedPrice" :min="0" :precision="2"
      /></el-form-item>
      <el-form-item label="总库存"
        ><el-input-number v-model="inventoryForm.totalCount" :min="0"
      /></el-form-item>
    </el-form>
    <template #footer
      ><el-button @click="inventoryVisible = false">取消</el-button
      ><el-button type="primary" :loading="saving" @click="saveInventory">保存</el-button></template
    >
  </Dialog>

  <Dialog v-model="serviceAssignVisible" title="派单" width="480px">
    <el-form :model="serviceAssignForm" label-width="100px">
      <el-form-item label="执行人" required>
        <el-select
          v-model="serviceAssignForm.assigneeId"
          placeholder="请选择执行人"
          filterable
          class="!w-1/1"
          :loading="serviceAssignLoading"
        >
          <el-option
            v-for="item in serviceEmployees"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-alert
        v-if="!serviceAssignLoading && serviceEmployees.length === 0"
        type="warning"
        :closable="false"
        title="当前酒店没有在职员工，请先在人员管理中新增员工"
      />
    </el-form>
    <template #footer>
      <el-button @click="serviceAssignVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="submitServiceAssign">确定</el-button>
    </template>
  </Dialog>

  <Dialog v-model="maintenanceCompleteVisible" title="完成维修" width="480px">
    <el-form :model="maintenanceCompleteForm" label-width="100px">
      <el-form-item label="维修费用">
        <el-input-number
          v-model="maintenanceCompleteForm.cost"
          :min="0"
          :precision="2"
          class="!w-1/1"
        />
      </el-form-item>
      <el-form-item label="完成备注">
        <el-input v-model="maintenanceCompleteForm.remark" type="textarea" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="maintenanceCompleteVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="submitMaintenanceComplete"
        >提交</el-button
      >
    </template>
  </Dialog>

  <Dialog v-model="maintenanceVisible" title="新增报修" width="520px">
    <el-form :model="maintenanceForm" label-width="100px">
      <el-form-item label="房间" required
        ><el-select v-model="maintenanceForm.roomId"
          ><el-option
            v-for="item in rooms"
            :key="item.id"
            :label="item.roomNo"
            :value="item.id" /></el-select
      ></el-form-item>
      <el-form-item label="故障类型" required
        ><el-input v-model="maintenanceForm.faultType"
      /></el-form-item>
      <el-form-item label="故障描述" required
        ><el-input v-model="maintenanceForm.faultDesc" type="textarea"
      /></el-form-item>
      <el-form-item label="优先级"
        ><el-input-number v-model="maintenanceForm.priority" :min="0" :max="3"
      /></el-form-item>
    </el-form>
    <template #footer
      ><el-button @click="maintenanceVisible = false">取消</el-button
      ><el-button type="primary" :loading="saving" @click="saveMaintenance"
        >提交</el-button
      ></template
    >
  </Dialog>

  <Dialog v-model="quickCheckInVisible" title="线下收款并快速入住" width="680px">
    <el-alert
      class="mb-16px"
      type="warning"
      :closable="false"
      title="该入口仅用于门店已经线下收款的当日到店订单，提交后会立即扣库存、记账并办理入住。"
    />
    <el-form :model="quickCheckInForm" label-width="105px">
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="房型" required>
            <el-select v-model="quickCheckInForm.spuId" class="!w-100%" @change="resetQuickRoom">
              <el-option
                v-for="item in roomTypeRows"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="价型">
            <el-select v-model="quickCheckInForm.skuId" clearable class="!w-100%">
              <el-option
                v-for="item in quickRatePlans"
                :key="item.id"
                :label="`${item.ratePlanName} ¥${(Number(item.price) / 100).toFixed(2)}`"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="实体房间" required>
            <el-select v-model="quickCheckInForm.roomId" class="!w-100%">
              <el-option
                v-for="item in quickAvailableRooms"
                :key="item.id"
                :label="item.roomNo"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="入住/离店" required>
            <el-date-picker
              v-model="quickStayRange"
              type="daterange"
              value-format="YYYY-MM-DD"
              :disabled-date="(date: Date) => dayjs(date).isBefore(dayjs(), 'day')"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="入住人" required
            ><el-input v-model="quickCheckInForm.guestName"
          /></el-form-item>
          <el-form-item label="手机号" required
            ><el-input v-model="quickCheckInForm.guestPhone" maxlength="11"
          /></el-form-item>
          <el-form-item label="入住人数"
            ><el-input-number v-model="quickCheckInForm.guestCount" :min="1" :max="20"
          /></el-form-item>
          <el-form-item label="实收金额" required
            ><el-input-number v-model="quickCheckInForm.totalPrice" :min="0.01" :precision="2"
          /></el-form-item>
          <el-form-item label="支付方式" required>
            <el-select v-model="quickCheckInForm.paymentMethod" class="!w-100%">
              <el-option label="现金" value="CASH" />
              <el-option label="微信" value="WECHAT" />
              <el-option label="支付宝" value="ALIPAY" />
              <el-option label="银行卡" value="BANK_CARD" />
              <el-option label="其他" value="OTHER" />
            </el-select>
          </el-form-item>
          <el-form-item label="收款备注"
            ><el-input v-model="quickCheckInForm.paymentRemark"
          /></el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button @click="quickCheckInVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="saveQuickCheckIn"
        >确认已收款并入住</el-button
      >
    </template>
  </Dialog>

  <Dialog v-model="orderDetailVisible" title="订单前台详情" width="900px">
    <el-descriptions :column="3" border v-loading="loading">
      <el-descriptions-item label="订单号">{{ orderDetail.orderNo }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{
        orderStatusLabel(orderDetail.status)
      }}</el-descriptions-item>
      <el-descriptions-item label="房间">{{ orderDetail.roomNo || '未分房' }}</el-descriptions-item>
      <el-descriptions-item label="入住人">{{ orderDetail.guestName }}</el-descriptions-item>
      <el-descriptions-item label="手机号">{{ orderDetail.guestPhone }}</el-descriptions-item>
      <el-descriptions-item label="实付"
        >¥{{ orderDetail.payAmount || orderDetail.totalPrice || 0 }}</el-descriptions-item
      >
      <el-descriptions-item label="押金"
        >¥{{ orderDetail.depositAmount || 0 }}</el-descriptions-item
      >
      <el-descriptions-item label="押金状态">{{
        depositStatusLabel(orderDetail.depositStatus)
      }}</el-descriptions-item>
      <el-descriptions-item label="入住日期"
        >{{ orderDetail.checkInDate }} 至 {{ orderDetail.checkOutDate }}</el-descriptions-item
      >
    </el-descriptions>
    <div class="front-desk-actions">
      <el-button v-if="[1, 2, 3].includes(orderDetail.status)" @click="openAssignRoom">
        {{ orderDetail.roomId ? '换房' : '分房' }}
      </el-button>
      <el-button v-if="[2, 3].includes(orderDetail.status)" @click="openExtension">续住</el-button>
      <el-button
        v-if="[1, 2, 3].includes(orderDetail.status) && orderDetail.depositStatus !== 1"
        @click="collectDeposit"
      >
        收取押金
      </el-button>
      <el-button v-if="orderDetail.depositStatus === 1" type="warning" @click="refundDeposit"
        >退还押金</el-button
      >
      <el-button type="primary" @click="openGuest()">新增入住人</el-button>
    </div>
    <el-table :data="orderDetail.guests || []" border>
      <el-table-column prop="name" label="姓名" />
      <el-table-column prop="phone" label="手机号" />
      <el-table-column prop="idType" label="证件类型" />
      <el-table-column prop="idNumber" label="证件号码" min-width="170" />
      <el-table-column label="实名核验" width="120">
        <template #default="{ row }">
          <el-tag
            :type="
              row.verificationStatus === 'VERIFIED'
                ? 'success'
                : row.verificationStatus === 'REJECTED'
                  ? 'danger'
                  : 'info'
            "
          >
            {{ row.verificationStatus || 'UNVERIFIED' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="主入住人" width="90">
        <template #default="{ row }">{{ row.isPrimary === 1 ? '是' : '否' }}</template>
      </el-table-column>
      <el-table-column label="操作" width="410">
        <template #default="{ row }">
          <el-button link type="primary" @click="revealGuestIdNumber(row)">查看证件</el-button>
          <el-button link type="success" @click="verifyGuest(row, true)">核验通过</el-button>
          <el-button link type="danger" @click="verifyGuest(row, false)">驳回</el-button>
          <el-button link type="primary" @click="openGuest(row)">编辑</el-button>
          <el-button link type="danger" @click="removeGuest(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
  </Dialog>

  <Dialog
    v-model="assignRoomVisible"
    :title="orderDetail.roomId ? '办理换房' : '办理分房'"
    width="500px"
  >
    <el-form label-width="90px">
      <el-form-item label="目标房间" required>
        <el-select v-model="assignRoomForm.roomId" class="!w-100%">
          <el-option
            v-for="item in assignableRooms"
            :key="item.id"
            :label="item.roomNo"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item v-if="orderDetail.roomId" label="换房原因" required>
        <el-input v-model="assignRoomForm.reason" type="textarea" maxlength="200" show-word-limit />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="assignRoomVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="saveAssignRoom">确认</el-button>
    </template>
  </Dialog>

  <Dialog v-model="guestVisible" :title="guestForm.id ? '编辑入住人' : '新增入住人'" width="560px">
    <el-form :model="guestForm" label-width="90px">
      <el-form-item label="姓名" required><el-input v-model="guestForm.name" /></el-form-item>
      <el-form-item label="手机号"
        ><el-input v-model="guestForm.phone" maxlength="11"
      /></el-form-item>
      <el-form-item label="证件类型">
        <el-select v-model="guestForm.idType" clearable>
          <el-option label="身份证" value="ID_CARD" />
          <el-option label="护照" value="PASSPORT" />
          <el-option label="其他" value="OTHER" />
        </el-select>
      </el-form-item>
      <el-form-item label="证件号码"><el-input v-model="guestForm.idNumber" /></el-form-item>
      <el-form-item label="性别">
        <el-radio-group v-model="guestForm.gender">
          <el-radio :value="0">未知</el-radio><el-radio :value="1">男</el-radio
          ><el-radio :value="2">女</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="主入住人"
        ><el-switch v-model="guestForm.isPrimary" :active-value="1" :inactive-value="0"
      /></el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="guestVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="saveGuest">保存</el-button>
    </template>
  </Dialog>

  <Dialog v-model="extensionVisible" title="办理续住" width="560px">
    <el-form :model="extensionForm" label-width="100px">
      <el-form-item label="原退房日期">{{ orderDetail.checkOutDate }}</el-form-item>
      <el-form-item label="新退房日期" required>
        <el-date-picker
          v-model="extensionForm.newCheckOutDate"
          value-format="YYYY-MM-DD"
          :disabled-date="
            (date: Date) => !dayjs(date).isAfter(dayjs(orderDetail.checkOutDate), 'day')
          "
          @change="loadExtensionQuote"
        />
      </el-form-item>
      <el-form-item label="续住方式">
        <el-radio-group v-model="extensionForm.extensionType" @change="loadExtensionQuote">
          <el-radio value="SAME_ROOM">原房续住</el-radio>
          <el-radio value="CHANGE_ROOM">到期换房</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item v-if="extensionForm.extensionType === 'CHANGE_ROOM'" label="目标房间" required>
        <el-select v-model="extensionForm.newRoomId" class="!w-100%" @change="loadExtensionQuote">
          <el-option
            v-for="item in extensionRooms"
            :key="item.id"
            :label="item.roomNo"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-descriptions v-if="extensionQuote.extensionAmount != null" :column="2" border>
        <el-descriptions-item label="续住晚数"
          >{{ extensionQuote.extensionDays }} 晚</el-descriptions-item
        >
        <el-descriptions-item label="应收金额"
          >¥{{ formatMoney(extensionQuote.extensionAmount) }}</el-descriptions-item
        >
      </el-descriptions>
      <el-alert
        class="mt-12px"
        type="warning"
        :closable="false"
        title="提交表示门店已线下收款；服务端将重新校验每日价格、库存、房间冲突并创建统一续住交易。"
      />
    </el-form>
    <template #footer>
      <el-button @click="extensionVisible = false">取消</el-button>
      <el-button
        type="primary"
        :loading="saving"
        :disabled="extensionQuote.extensionAmount == null"
        @click="saveExtension"
      >
        确认收款并续住
      </el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import * as HotelApi from '@/api/merchant/hotel'
import download from '@/utils/download'

defineOptions({ name: 'MerchantHotelOperation' })

const route = useRoute()
const message = useMessage()
const section = computed(() => String(route.meta.hotelSection || 'dashboard'))
const titles: Record<string, string> = {
  dashboard: '经营看板',
  'room-types': '房型与价型',
  rooms: '实体房间',
  inventory: '日历量价',
  orders: '预订订单',
  services: '客房服务',
  finance: '酒店财务',
  settings: '运营策略'
}
const sectionTitle = computed(() => titles[section.value] || '酒店经营')
const loading = ref(false)
const saving = ref(false)
const shops = ref<HotelApi.HotelShopOption[]>([])
const shopId = ref<string | number>()
const roomTypes = ref<HotelApi.HotelRoomTypeOption[]>([])
const facilities = ref<HotelApi.HotelFacilityOption[]>([])
const roomStates = [
  { value: 0, label: '空闲' },
  { value: 1, label: '预订' },
  { value: 2, label: '入住' },
  { value: 3, label: '退房' },
  { value: 4, label: '清洁' },
  { value: 5, label: '维修' },
  { value: 6, label: '锁定' }
]
const roomStateLabel = (value: number) =>
  roomStates.find((item) => item.value === value)?.label || '未知'

const bedTypes = [
  { value: 'KING', label: '大床' },
  { value: 'TWIN', label: '双床' },
  { value: 'FAMILY', label: '家庭房' },
  { value: 'SUITE', label: '套房' },
  { value: 'MULTI', label: '多床房' },
  { value: 'OTHER', label: '其他' }
]
const bedTypeLabel = (value: string) =>
  bedTypes.find((item) => item.value === value)?.label || value
const auditStatuses: Record<number, { label: string; type: string }> = {
  0: { label: '草稿', type: 'info' },
  1: { label: '审核中', type: 'warning' },
  2: { label: '审核通过', type: 'success' },
  3: { label: '审核驳回', type: 'danger' },
  4: { label: '已上架', type: 'success' },
  5: { label: '已下架', type: 'info' }
}
const auditStatusMeta = (value: number) => auditStatuses[value] || auditStatuses[0]

const roomTypeRows = ref<HotelApi.HotelRoomType[]>([])
const roomTypeVisible = ref(false)
const roomTypeImportInput = ref<HTMLInputElement>()
const newRatePlan = (maxGuests = 2) => ({
  ratePlanName: '标准价',
  priceYuan: 0,
  breakfastCount: 0,
  cancellationPolicy: '入住日前一天 18:00 前可免费取消',
  minStay: 1,
  maxStay: 30,
  maxGuests,
  saleStatus: 1
})
const roomTypeForm = reactive<any>({
  id: undefined,
  name: '',
  keyword: '',
  introduction: '',
  description: '',
  picUrl: '',
  sliderPicsText: '',
  sort: 0,
  bedType: 'KING',
  maxGuests: 2,
  roomSize: 30,
  facilityCodesText: 'WIFI',
  skus: [newRatePlan()]
})
const roomTypeMinPrice = (row: HotelApi.HotelRoomType) => {
  const prices = (row.skus || []).map((sku) => Number(sku.price || 0)).filter((price) => price > 0)
  return prices.length ? (Math.min(...prices) / 100).toFixed(2) : '0.00'
}
const roomTypeFacilityCodes = computed({
  get: () => splitText(roomTypeForm.facilityCodesText, /[,，]/),
  set: (codes: string[]) => {
    roomTypeForm.facilityCodesText = codes.join(',')
  }
})
const openRoomType = (row?: HotelApi.HotelRoomType) => {
  Object.assign(
    roomTypeForm,
    row
      ? {
          id: row.id,
          name: row.name,
          keyword: row.keyword || row.name,
          introduction: row.introduction || '',
          description: row.description || '',
          picUrl: row.picUrl || '',
          sliderPicsText: (row.sliderPicUrls || []).join('\n'),
          sort: row.sort || 0,
          bedType: row.bedType || 'KING',
          maxGuests: row.maxGuests || 2,
          roomSize: row.roomSize || 30,
          facilityCodesText: (row.facilityCodes || []).join(','),
          skus: (row.skus || []).map((sku) => ({
            ...sku,
            priceYuan: Number(sku.price || 0) / 100
          }))
        }
      : {
          id: undefined,
          name: '',
          keyword: '',
          introduction: '',
          description: '',
          picUrl: '',
          sliderPicsText: '',
          sort: 0,
          bedType: 'KING',
          maxGuests: 2,
          roomSize: 30,
          facilityCodesText: 'WIFI',
          skus: [newRatePlan()]
        }
  )
  if (roomTypeForm.skus.length === 0) roomTypeForm.skus = [newRatePlan(roomTypeForm.maxGuests)]
  roomTypeVisible.value = true
}
const addRatePlan = () => {
  const plan = newRatePlan(roomTypeForm.maxGuests)
  plan.ratePlanName = `价型${roomTypeForm.skus.length + 1}`
  roomTypeForm.skus.push(plan)
}
const removeRatePlan = (index: number) => {
  if (roomTypeForm.skus.length <= 1) return message.warning('每个房型至少保留一个价型')
  roomTypeForm.skus.splice(index, 1)
}
const splitText = (value: string, pattern: RegExp) =>
  String(value || '')
    .split(pattern)
    .map((item) => item.trim())
    .filter(Boolean)
const saveRoomType = async () => {
  const required = [
    roomTypeForm.name,
    roomTypeForm.keyword,
    roomTypeForm.introduction,
    roomTypeForm.description,
    roomTypeForm.picUrl,
    roomTypeForm.bedType
  ]
  if (required.some((item) => !String(item || '').trim()))
    return message.warning('请完整填写房型资料')
  const sliderPicUrls = splitText(roomTypeForm.sliderPicsText, /\r?\n/)
  const urls = [roomTypeForm.picUrl, ...sliderPicUrls]
  if (urls.some((url) => !/^https?:\/\//i.test(url)))
    return message.warning('房型图片必须使用 http(s) URL')
  const names = roomTypeForm.skus.map((sku: any) => String(sku.ratePlanName || '').trim())
  const invalidSku = roomTypeForm.skus.some(
    (sku: any) =>
      !sku.ratePlanName ||
      Number(sku.priceYuan) <= 0 ||
      !sku.cancellationPolicy ||
      Number(sku.minStay) < 1 ||
      Number(sku.maxStay) < Number(sku.minStay)
  )
  if (invalidSku || new Set(names).size !== names.length)
    return message.warning('请检查价型名称、价格、取消规则和入住晚数，价型名称不能重复')
  const data = {
    id: roomTypeForm.id,
    name: roomTypeForm.name.trim(),
    keyword: roomTypeForm.keyword.trim(),
    introduction: roomTypeForm.introduction.trim(),
    description: roomTypeForm.description.trim(),
    picUrl: roomTypeForm.picUrl.trim(),
    sliderPicUrls,
    sort: roomTypeForm.sort,
    bedType: roomTypeForm.bedType,
    maxGuests: roomTypeForm.maxGuests,
    roomSize: roomTypeForm.roomSize,
    facilityCodes: splitText(roomTypeForm.facilityCodesText, /[,，]/),
    skus: roomTypeForm.skus.map((sku: any) => ({
      ratePlanName: sku.ratePlanName.trim(),
      price: Math.round(Number(sku.priceYuan) * 100),
      marketPrice: Math.round(Number(sku.priceYuan) * 100),
      costPrice: Math.round(Number(sku.priceYuan) * 100),
      picUrl: roomTypeForm.picUrl.trim(),
      stock: Number(sku.stock || 0),
      breakfastCount: Number(sku.breakfastCount || 0),
      cancellationPolicy: sku.cancellationPolicy.trim(),
      minStay: Number(sku.minStay),
      maxStay: Number(sku.maxStay),
      maxGuests: Number(sku.maxGuests),
      saleStatus: Number(sku.saleStatus)
    }))
  }
  saving.value = true
  try {
    await (roomTypeForm.id
      ? HotelApi.updateRoomType(shopId.value, data)
      : HotelApi.createRoomType(shopId.value, data))
    message.success('房型已保存并进入平台审核')
    roomTypeVisible.value = false
    await loadRoomTypes()
  } finally {
    saving.value = false
  }
}
const changeRoomTypeSaleStatus = async (row: HotelApi.HotelRoomType, onSale: boolean) => {
  await message.confirm(onSale ? '确认上架该房型吗？' : '确认下架该房型吗？')
  await HotelApi.updateRoomTypeSaleStatus(shopId.value, row.id, onSale)
  message.success(onSale ? '房型已上架' : '房型已下架')
  await loadRoomTypes()
}
const downloadRoomTypeTemplate = async () => {
  download.excel(await HotelApi.getRoomTypeImportTemplate(), '酒店房型导入模板.xls')
}
const exportRoomTypes = async () => {
  if (!shopId.value) return message.warning('请先选择酒店')
  download.excel(await HotelApi.exportRoomTypes(shopId.value), '酒店房型与价型.xls')
}
const importRoomTypeFile = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file || !shopId.value) return
  saving.value = true
  try {
    const result = await HotelApi.importRoomTypes(shopId.value, file)
    message.success(`导入完成：新增 ${result.createdCount} 个，更新 ${result.updatedCount} 个`)
    await loadRoomTypes()
  } finally {
    saving.value = false
  }
}

const overview = ref<any>({})
const dashboardRange = ref([
  dayjs().startOf('month').format('YYYY-MM-DD'),
  dayjs().format('YYYY-MM-DD')
])
const dashboardCards = computed(() => [
  { label: '营收', value: `¥${Number(overview.value.revenue || 0).toFixed(2)}` },
  { label: '平均入住率', value: `${Number(overview.value.occupancyRate || 0).toFixed(2)}%` },
  { label: '新增预订', value: overview.value.newBookingCount || 0 },
  { label: '入住', value: overview.value.checkInCount || 0 },
  { label: '清洁任务', value: overview.value.cleaningCount || 0 },
  { label: '维修任务', value: overview.value.maintenanceCount || 0 }
])
const loadDashboard = async () => {
  if (!shopId.value || dashboardRange.value.length !== 2) return
  overview.value = await HotelApi.getStatisticsOverview({
    shopId: shopId.value,
    startDate: dashboardRange.value[0],
    endDate: dashboardRange.value[1]
  })
}

const rooms = ref<any[]>([])
const roomTotal = ref(0)
const roomQuery = reactive<any>({ pageNo: 1, pageSize: 10, roomNo: '', roomState: undefined })
const loadRooms = async () => {
  if (!shopId.value) return
  const data = await HotelApi.getRoomPage({
    ...roomQuery,
    shopId: shopId.value,
    pageSize: roomQuery.pageSize
  })
  rooms.value = data.list || []
  roomTotal.value = data.total || 0
}
const loadAllRooms = async () => {
  if (!shopId.value) return
  const data = await HotelApi.getRoomPage({ shopId: shopId.value, pageNo: 1, pageSize: 200 })
  rooms.value = data.list || []
}
const roomVisible = ref(false)
const roomForm = reactive<any>({
  id: undefined,
  spuId: undefined,
  roomNo: '',
  floor: undefined,
  roomState: 0,
  remark: ''
})
const openRoom = (row?: any) => {
  Object.assign(
    roomForm,
    row
      ? { ...row }
      : {
          id: undefined,
          spuId: roomTypes.value[0]?.id,
          roomNo: '',
          floor: undefined,
          roomState: 0,
          remark: ''
        }
  )
  roomVisible.value = true
}
const saveRoom = async () => {
  if (!roomForm.spuId || !roomForm.roomNo) return message.warning('请填写房型和房间号')
  saving.value = true
  try {
    const data = { ...roomForm, shopId: shopId.value }
    await (roomForm.id ? HotelApi.updateRoom(data) : HotelApi.createRoom(data))
    message.success('房间已保存')
    roomVisible.value = false
    await loadRooms()
  } finally {
    saving.value = false
  }
}
const removeRoom = async (row: any) => {
  await message.delConfirm()
  await HotelApi.deleteRoom(row.id, shopId.value)
  await loadRooms()
}

const inventory = ref<any[]>([])
const inventoryQuery = reactive<any>({
  spuId: undefined,
  range: [dayjs().format('YYYY-MM-DD'), dayjs().add(13, 'day').format('YYYY-MM-DD')]
})
const loadInventory = async () => {
  if (!shopId.value || !inventoryQuery.spuId) return
  inventory.value = await HotelApi.getPriceCalendar({
    shopId: shopId.value,
    spuId: inventoryQuery.spuId,
    startDate: inventoryQuery.range[0],
    endDate: inventoryQuery.range[1]
  })
}
const inventoryVisible = ref(false)
const inventoryForm = reactive({ price: 0, extraBedPrice: 0, totalCount: 0 })
const openInventoryBatch = () => {
  if (!inventoryQuery.spuId) return message.warning('请先选择房型')
  inventoryVisible.value = true
}
const saveInventory = async () => {
  const rows: any[] = []
  for (
    let date = dayjs(inventoryQuery.range[0]);
    !date.isAfter(dayjs(inventoryQuery.range[1]));
    date = date.add(1, 'day')
  ) {
    rows.push({
      shopId: shopId.value,
      spuId: inventoryQuery.spuId,
      date: date.format('YYYY-MM-DD'),
      ...inventoryForm
    })
  }
  saving.value = true
  try {
    await HotelApi.batchSetPriceInventory(shopId.value!, rows)
    inventoryVisible.value = false
    message.success('量价已保存')
    await loadInventory()
  } finally {
    saving.value = false
  }
}

const orders = ref<any[]>([])
const orderTotal = ref(0)
const orderQuery = reactive<any>({ pageNo: 1, pageSize: 10, orderNo: '', guestName: '' })
const orderStatuses: Record<number, string> = {
  0: '待支付',
  1: '已支付',
  2: '已确认',
  3: '已入住',
  4: '已完成',
  5: '已取消'
}
const orderStatusLabel = (value: number) => orderStatuses[value] || '未知'
const loadOrders = async () => {
  const data = await HotelApi.getBookingOrderPage({ ...orderQuery, shopId: shopId.value })
  orders.value = data.list || []
  orderTotal.value = data.total || 0
}
const orderAction = async (row: any, action: 'confirm' | 'cancel' | 'check-in' | 'check-out') => {
  let reason: string | undefined
  if (action === 'cancel') reason = (await message.prompt('请输入取消原因', '取消订单')).value
  await HotelApi.operateBookingOrder(row.id, action, shopId.value, reason)
  message.success('订单状态已更新')
  await loadOrders()
}

const quickCheckInVisible = ref(false)
const quickStayRange = ref([
  dayjs().format('YYYY-MM-DD'),
  dayjs().add(1, 'day').format('YYYY-MM-DD')
])
const quickCheckInForm = reactive<any>({
  spuId: undefined,
  skuId: undefined,
  roomId: undefined,
  guestName: '',
  guestPhone: '',
  guestCount: 1,
  totalPrice: 0,
  paymentMethod: 'CASH',
  paymentRemark: ''
})
const quickRatePlans = computed(
  () =>
    roomTypeRows.value.find((item) => String(item.id) === String(quickCheckInForm.spuId))?.skus ||
    []
)
const quickAvailableRooms = computed(() =>
  rooms.value.filter(
    (item) => item.roomState === 0 && String(item.spuId) === String(quickCheckInForm.spuId)
  )
)
const resetQuickRoom = () => {
  quickCheckInForm.skuId = quickRatePlans.value[0]?.id
  quickCheckInForm.roomId = quickAvailableRooms.value[0]?.id
  if (quickRatePlans.value[0]?.price) {
    quickCheckInForm.totalPrice = Number(quickRatePlans.value[0].price) / 100
  }
}
const openQuickCheckIn = async () => {
  await loadAllRooms()
  Object.assign(quickCheckInForm, {
    spuId: roomTypeRows.value[0]?.id,
    skuId: undefined,
    roomId: undefined,
    guestName: '',
    guestPhone: '',
    guestCount: 1,
    totalPrice: 0,
    paymentMethod: 'CASH',
    paymentRemark: ''
  })
  quickStayRange.value = [dayjs().format('YYYY-MM-DD'), dayjs().add(1, 'day').format('YYYY-MM-DD')]
  resetQuickRoom()
  quickCheckInVisible.value = true
}
const saveQuickCheckIn = async () => {
  if (
    !quickCheckInForm.spuId ||
    !quickCheckInForm.roomId ||
    !quickCheckInForm.guestName.trim() ||
    !/^1\d{10}$/.test(quickCheckInForm.guestPhone) ||
    Number(quickCheckInForm.totalPrice) <= 0 ||
    quickStayRange.value.length !== 2
  ) {
    return message.warning('请完整填写房型、房间、入住人、手机号、日期和实收金额')
  }
  saving.value = true
  try {
    await HotelApi.quickCheckIn({
      ...quickCheckInForm,
      shopId: shopId.value,
      checkInDate: quickStayRange.value[0],
      checkOutDate: quickStayRange.value[1],
      roomCount: 1,
      status: 0
    })
    message.success('线下订单已记账并办理入住')
    quickCheckInVisible.value = false
    await loadOrders()
  } finally {
    saving.value = false
  }
}

const orderDetailVisible = ref(false)
const orderDetail = reactive<any>({ guests: [] })
const depositStatuses: Record<number, string> = {
  0: '未收取',
  1: '已收取',
  2: '已退还',
  3: '部分退还'
}
const depositStatusLabel = (value: number) => depositStatuses[value] || '未收取'
const refreshOrderDetail = async () => {
  if (!orderDetail.id) return
  Object.assign(orderDetail, await HotelApi.getBookingOrderDetail(orderDetail.id, shopId.value))
}
const openOrderDetail = async (row: any) => {
  orderDetail.id = row.id
  loading.value = true
  try {
    await Promise.all([refreshOrderDetail(), loadAllRooms()])
    orderDetailVisible.value = true
  } finally {
    loading.value = false
  }
}
const assignRoomVisible = ref(false)
const assignRoomForm = reactive<any>({ roomId: undefined, reason: '' })
const assignableRooms = computed(() =>
  rooms.value.filter(
    (item) => item.roomState === 0 && String(item.spuId) === String(orderDetail.spuId)
  )
)
const openAssignRoom = async () => {
  await loadAllRooms()
  Object.assign(assignRoomForm, { roomId: assignableRooms.value[0]?.id, reason: '' })
  assignRoomVisible.value = true
}
const saveAssignRoom = async () => {
  if (!assignRoomForm.roomId || (orderDetail.roomId && !assignRoomForm.reason.trim())) {
    return message.warning(orderDetail.roomId ? '请选择目标房间并填写换房原因' : '请选择目标房间')
  }
  saving.value = true
  try {
    await HotelApi.assignBookingRoom({
      shopId: shopId.value,
      orderId: orderDetail.id,
      roomId: assignRoomForm.roomId,
      reason: assignRoomForm.reason
    })
    assignRoomVisible.value = false
    message.success(orderDetail.roomId ? '换房完成，原房已生成清洁任务' : '分房完成')
    await Promise.all([refreshOrderDetail(), loadOrders()])
  } finally {
    saving.value = false
  }
}
const collectDeposit = async () => {
  const amount = Number((await message.prompt('请输入线下实收押金金额', '收取押金')).value)
  if (!Number.isFinite(amount) || amount <= 0) return message.warning('押金金额必须大于 0')
  await HotelApi.collectOrderDeposit(shopId.value, orderDetail.id, amount)
  message.success('押金已通过统一订单入账')
  await refreshOrderDetail()
}
const refundDeposit = async () => {
  const reason = (await message.prompt('请输入退押金原因', '退还押金')).value.trim()
  if (!reason) return message.warning('退押金原因不能为空')
  await HotelApi.refundOrderDeposit(shopId.value, orderDetail.id, reason)
  message.success('押金已全额退还')
  await refreshOrderDetail()
}

const guestVisible = ref(false)
const guestForm = reactive<any>({
  id: undefined,
  name: '',
  phone: '',
  idType: '',
  idNumber: '',
  gender: 0,
  isPrimary: 0
})
const openGuest = (row?: any) => {
  Object.assign(
    guestForm,
    row
      ? { ...row }
      : { id: undefined, name: '', phone: '', idType: '', idNumber: '', gender: 0, isPrimary: 0 }
  )
  guestVisible.value = true
}
const saveGuest = async () => {
  if (!guestForm.name.trim() || (guestForm.phone && !/^1\d{10}$/.test(guestForm.phone))) {
    return message.warning('请填写入住人姓名并检查手机号')
  }
  saving.value = true
  try {
    const data = { ...guestForm, orderId: orderDetail.id, roomId: orderDetail.roomId }
    await (guestForm.id
      ? HotelApi.updateOrderGuest(shopId.value, data)
      : HotelApi.createOrderGuest(shopId.value, data))
    guestVisible.value = false
    message.success('入住人已保存')
    await refreshOrderDetail()
  } finally {
    saving.value = false
  }
}
const removeGuest = async (row: any) => {
  await message.delConfirm()
  await HotelApi.deleteOrderGuest(shopId.value, row.id)
  message.success('入住人已删除')
  await refreshOrderDetail()
}

const revealGuestIdNumber = async (row: any) => {
  await message.confirm('查看完整证件号将记录安全审计日志，是否继续？')
  row.idNumber = await HotelApi.viewGuestIdNumber(shopId.value, row.id)
}
const verifyGuest = async (row: any, approved: boolean) => {
  const remark = (
    await message.prompt(approved ? '请输入核验依据' : '请输入驳回原因', '实名核验')
  ).value.trim()
  if (!remark) return
  await HotelApi.verifyGuestIdentity({
    shopId: shopId.value,
    guestId: row.id,
    approved,
    method: 'MANUAL_DOCUMENT',
    remark
  })
  message.success(approved ? '实名核验已通过' : '实名核验已驳回')
  await refreshOrderDetail()
}
const extensionVisible = ref(false)
const extensionForm = reactive<any>({
  newCheckOutDate: '',
  extensionType: 'SAME_ROOM',
  newRoomId: undefined
})
const extensionQuote = reactive<any>({ extensionAmount: null })
const extensionRooms = computed(() =>
  rooms.value.filter(
    (item) =>
      [0, 1].includes(item.roomState) &&
      String(item.spuId) === String(orderDetail.spuId) &&
      String(item.id) !== String(orderDetail.roomId)
  )
)
const extensionPayload = () => ({
  shopId: shopId.value,
  orderId: orderDetail.id,
  newCheckOutDate: extensionForm.newCheckOutDate,
  extensionType: extensionForm.extensionType,
  newRoomId: extensionForm.extensionType === 'CHANGE_ROOM' ? extensionForm.newRoomId : undefined
})
const openExtension = async () => {
  await loadAllRooms()
  Object.assign(extensionForm, {
    newCheckOutDate: dayjs(orderDetail.checkOutDate).add(1, 'day').format('YYYY-MM-DD'),
    extensionType: 'SAME_ROOM',
    newRoomId: extensionRooms.value[0]?.id
  })
  extensionQuote.extensionAmount = null
  extensionVisible.value = true
  await loadExtensionQuote()
}
const loadExtensionQuote = async () => {
  extensionQuote.extensionAmount = null
  if (
    !extensionForm.newCheckOutDate ||
    (extensionForm.extensionType === 'CHANGE_ROOM' && !extensionForm.newRoomId)
  )
    return
  Object.assign(extensionQuote, await HotelApi.quoteOrderExtension(extensionPayload()))
}
const saveExtension = async () => {
  if (extensionQuote.extensionAmount == null) return
  await message.confirm(
    `确认已线下收取续住费用 ¥${formatMoney(extensionQuote.extensionAmount)} 吗？`
  )
  saving.value = true
  try {
    await HotelApi.createAndPayOrderExtension(extensionPayload())
    extensionVisible.value = false
    message.success('续住交易已创建并确认收款')
    await Promise.all([refreshOrderDetail(), loadOrders()])
  } finally {
    saving.value = false
  }
}

const cleaning = ref<any[]>([])
const maintenance = ref<any[]>([])
const loadServices = async () => {
  const [cleaningData, maintenanceData] = await Promise.all([
    HotelApi.getCleaningPage({ shopId: shopId.value, pageNo: 1, pageSize: 100 }),
    HotelApi.getMaintenancePage({ shopId: shopId.value, pageNo: 1, pageSize: 100 })
  ])
  cleaning.value = cleaningData.list || []
  maintenance.value = maintenanceData.list || []
  await loadAllRooms()
}
const cleaningAction = async (row: any, action: string) => {
  await HotelApi.operateCleaning({
    shopId: shopId.value,
    id: row.id,
    action,
    verifyUser: action === 'verify' ? '商家验收' : undefined
  })
  await loadServices()
}
const maintenanceAction = async (row: any, action: string) => {
  await HotelApi.operateMaintenance({
    shopId: shopId.value,
    id: row.id,
    action,
    verifyUser: action === 'verify' ? '商家验收' : undefined
  })
  await loadServices()
}
const serviceAssignVisible = ref(false)
const serviceAssignLoading = ref(false)
const serviceAssignType = ref<'cleaning' | 'maintenance'>('cleaning')
const serviceAssignRow = ref<any>()
const serviceEmployees = ref<any[]>([])
const serviceAssignForm = reactive<{ assigneeId?: string | number }>({ assigneeId: undefined })

const openServiceAssign = async (row: any, type: 'cleaning' | 'maintenance') => {
  serviceAssignRow.value = row
  serviceAssignType.value = type
  serviceAssignForm.assigneeId = undefined
  serviceAssignVisible.value = true
  serviceAssignLoading.value = true
  try {
    serviceEmployees.value = (await HotelApi.getServiceEmployeeOptions(shopId.value)) || []
  } finally {
    serviceAssignLoading.value = false
  }
}

const submitServiceAssign = async () => {
  const assignee = serviceEmployees.value.find((e) => e.id === serviceAssignForm.assigneeId)
  if (!assignee) return message.warning('请选择执行人')
  saving.value = true
  try {
    const payload = {
      shopId: shopId.value,
      id: serviceAssignRow.value.id,
      action: 'assign',
      assigneeId: assignee.id,
      assigneeName: assignee.name
    }
    if (serviceAssignType.value === 'cleaning') await HotelApi.operateCleaning(payload)
    else await HotelApi.operateMaintenance(payload)
    serviceAssignVisible.value = false
    message.success('派单成功')
    await loadServices()
  } finally {
    saving.value = false
  }
}

const maintenanceCompleteVisible = ref(false)
const maintenanceCompleteRow = ref<any>()
const maintenanceCompleteForm = reactive<any>({ cost: 0, remark: '' })
const openMaintenanceComplete = (row: any) => {
  maintenanceCompleteRow.value = row
  maintenanceCompleteForm.cost = row.cost ?? 0
  maintenanceCompleteForm.remark = ''
  maintenanceCompleteVisible.value = true
}
const submitMaintenanceComplete = async () => {
  saving.value = true
  try {
    await HotelApi.operateMaintenance({
      shopId: shopId.value,
      id: maintenanceCompleteRow.value.id,
      action: 'complete',
      cost: maintenanceCompleteForm.cost,
      remark: maintenanceCompleteForm.remark || undefined
    })
    maintenanceCompleteVisible.value = false
    message.success('维修已完成')
    await loadServices()
  } finally {
    saving.value = false
  }
}
const removeMaintenance = async (row: any) => {
  try {
    await message.confirm(`确认删除维修工单 ${row.orderNo || ''}？`)
    await HotelApi.deleteMaintenance(row.id, shopId.value)
    message.success('删除成功')
    await loadServices()
  } catch {}
}

const financeRange = ref([
  dayjs().startOf('month').format('YYYY-MM-DD'),
  dayjs().format('YYYY-MM-DD')
])
const financeTab = ref('settlement')
const financeSummary = reactive<any>({})
const formatMoney = (value: any) => Number(value || 0).toFixed(2)
const financeCards = computed(() => [
  { label: '支付收入', value: `¥${formatMoney(financeSummary.grossIncome)}` },
  { label: '退款冲正', value: `¥${formatMoney(financeSummary.refundAmount)}` },
  { label: '净平台佣金', value: `¥${formatMoney(financeSummary.platformFee)}` },
  { label: '净商户应收', value: `¥${formatMoney(financeSummary.merchantReceivable)}` },
  { label: '待结算', value: `¥${formatMoney(financeSummary.walletPendingAmount)}` },
  { label: '可提现', value: `¥${formatMoney(financeSummary.walletUsableAmount)}` },
  { label: '已提现', value: `¥${formatMoney(financeSummary.walletWithdrawnAmount)}` },
  { label: '提现冻结', value: `¥${formatMoney(financeSummary.walletFreezingAmount)}` },
  { label: '钱包累计', value: `¥${formatMoney(financeSummary.walletTotalAmount)}` },
  { label: '待抵扣欠款', value: `¥${formatMoney(financeSummary.walletDebtAmount)}` }
])
const settlementRows = ref<any[]>([])
const settlementTotal = ref(0)
const settlementQuery = reactive({ pageNo: 1, pageSize: 10 })
const billRows = ref<any[]>([])
const billTotal = ref(0)
const billQuery = reactive({ pageNo: 1, pageSize: 10 })
const invoiceRows = ref<any[]>([])
const invoiceTotal = ref(0)
const invoiceQuery = reactive({ pageNo: 1, pageSize: 10 })
const batchRows = ref<any[]>([])
const batchTotal = ref(0)
const batchQuery = reactive({ pageNo: 1, pageSize: 10 })
const financeDateParams = () => ({
  shopId: shopId.value,
  startDate: financeRange.value[0],
  endDate: financeRange.value[1]
})
const loadSettlementDetails = async () => {
  const data = await HotelApi.getSettlementDetailPage({
    ...settlementQuery,
    ...financeDateParams()
  })
  settlementRows.value = data.list || []
  settlementTotal.value = data.total || 0
}
const loadBills = async () => {
  const data = await HotelApi.getMerchantBillPage({ ...billQuery, shopId: shopId.value })
  billRows.value = data.list || []
  billTotal.value = data.total || 0
}
const loadBatches = async () => {
  const data = await HotelApi.getSettlementBatchPage({ ...batchQuery, shopId: shopId.value })
  batchRows.value = data.list || []
  batchTotal.value = data.total || 0
}
const loadInvoices = async () => {
  const data: any = await HotelApi.getInvoicePage({
    ...invoiceQuery,
    shopId: shopId.value
  })
  invoiceRows.value = data.list || []
  invoiceTotal.value = data.total || 0
}
const issueInvoice = async (row: any) => {
  const invoiceNo = (await message.prompt('请输入发票号码', '开具电子发票')).value.trim()
  const invoiceFileUrl = (
    await message.prompt('请输入电子发票 http(s) 地址', '发票文件')
  ).value.trim()
  if (!invoiceNo || !/^https?:\/\//.test(invoiceFileUrl))
    return message.warning('发票号和文件地址不正确')
  await HotelApi.issueInvoice({ id: row.id, shopId: shopId.value, invoiceNo, invoiceFileUrl })
  message.success('发票已开具')
  await loadInvoices()
}
const rejectInvoice = async (row: any) => {
  const reason = (await message.prompt('请输入驳回原因', '驳回发票申请')).value.trim()
  if (!reason) return
  await HotelApi.rejectInvoice({ id: row.id, shopId: shopId.value, reason })
  await loadInvoices()
}
const completeInvoiceRed = async (row: any) => {
  const reason = (await message.prompt('请输入红字发票号', '完成冲红')).value.trim()
  if (!reason) return
  await HotelApi.completeInvoiceRed({ id: row.id, shopId: shopId.value, reason })
  await loadInvoices()
}
const loadFinance = async () => {
  if (!shopId.value || financeRange.value.length !== 2) return
  Object.assign(financeSummary, await HotelApi.getFinanceSummary(financeDateParams()))
  if (financeTab.value === 'settlement') await loadSettlementDetails()
  if (financeTab.value === 'batch') await loadBatches()
  if (financeTab.value === 'bill') await loadBills()
  if (financeTab.value === 'invoice') await loadInvoices()
}
const maintenanceVisible = ref(false)
const maintenanceForm = reactive<any>({
  roomId: undefined,
  faultType: '',
  faultDesc: '',
  priority: 1
})
const saveMaintenance = async () => {
  if (!maintenanceForm.roomId || !maintenanceForm.faultType || !maintenanceForm.faultDesc)
    return message.warning('请完整填写报修信息')
  saving.value = true
  try {
    await HotelApi.createMaintenance({ ...maintenanceForm, shopId: shopId.value, status: 0 })
    maintenanceVisible.value = false
    message.success('报修已提交')
    await loadServices()
  } finally {
    saving.value = false
  }
}

const policy = reactive<any>({})
const loadPolicy = async () => Object.assign(policy, await HotelApi.getShopPolicy(shopId.value))
const profileChangeForm = reactive<any>({
  hotelName: '',
  address: '',
  qualificationJson: '{}',
  settlementSubject: '',
  reason: ''
})
const profileChanges = ref<any[]>([])
const loadProfileChanges = async () => {
  const data: any = await HotelApi.getProfileChangePage({
    shopId: shopId.value,
    pageNo: 1,
    pageSize: 20
  })
  profileChanges.value = data.list || []
}
const submitProfileChange = async () => {
  if (
    !profileChangeForm.hotelName ||
    !profileChangeForm.address ||
    !profileChangeForm.settlementSubject ||
    !profileChangeForm.reason
  ) {
    message.warning('请完整填写敏感资料和变更原因')
    return
  }
  saving.value = true
  try {
    await HotelApi.submitProfileChange({ ...profileChangeForm, shopId: shopId.value })
    message.success('资料变更已提交平台审核')
    await loadProfileChanges()
  } finally {
    saving.value = false
  }
}
const savePolicy = async () => {
  saving.value = true
  try {
    await HotelApi.updateShopPolicy({ ...policy, shopId: shopId.value })
    message.success('运营策略已保存')
  } finally {
    saving.value = false
  }
}

const loadRoomTypes = async () => {
  const [roomTypeData, facilityData] = await Promise.all([
    HotelApi.getRoomTypes(shopId.value),
    HotelApi.getFacilities(shopId.value)
  ])
  roomTypeRows.value = roomTypeData || []
  facilities.value = facilityData || []
  roomTypes.value = roomTypeRows.value
  if (!inventoryQuery.spuId) inventoryQuery.spuId = roomTypes.value[0]?.id
}
const reload = async () => {
  if (!shopId.value) return
  localStorage.setItem('hotelShopId', String(shopId.value))
  loading.value = true
  try {
    await loadRoomTypes()
    if (section.value === 'dashboard') await loadDashboard()
    if (section.value === 'room-types') return
    if (section.value === 'rooms') await loadRooms()
    if (section.value === 'inventory') await loadInventory()
    if (section.value === 'orders') await loadOrders()
    if (section.value === 'services') await loadServices()
    if (section.value === 'finance') await loadFinance()
    if (section.value === 'settings') await Promise.all([loadPolicy(), loadProfileChanges()])
  } finally {
    loading.value = false
  }
}

watch(section, reload)
onMounted(async () => {
  shops.value = (await HotelApi.getAccessibleShops()) || []
  const stored = localStorage.getItem('hotelShopId')
  shopId.value = shops.value.find((item) => String(item.id) === stored)?.id || shops.value[0]?.id
  await reload()
})
</script>

<style scoped>
.hotel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.hotel-head h2 {
  margin: 0 0 6px;
}
.hotel-head p {
  margin: 0;
  color: var(--el-text-color-secondary);
}
.metric-card {
  margin-bottom: 16px;
}
.metric-card span,
.metric-card strong {
  display: block;
}
.metric-card span {
  color: var(--el-text-color-secondary);
}
.metric-card strong {
  margin-top: 10px;
  font-size: 24px;
}
.room-type-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
}
.room-type-actions {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
}
.room-type-toolbar .el-alert {
  flex: 1;
}
.room-type-name {
  display: flex;
  align-items: center;
  gap: 10px;
}
.room-type-name .el-image {
  width: 64px;
  height: 48px;
  border-radius: 4px;
}
.room-type-name strong,
.room-type-name small {
  display: block;
}
.front-desk-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 16px 0;
}
.finance-filter {
  display: flex;
  gap: 12px;
}
.room-type-name small {
  max-width: 180px;
  margin-top: 5px;
  overflow: hidden;
  color: var(--el-text-color-secondary);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stay-range {
  display: flex;
  align-items: center;
  gap: 4px;
}
.stay-range .el-input-number {
  width: 72px;
}
.policy-form {
  max-width: 720px;
}
</style>
